import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
  typeof (value as PromiseLike<unknown> | undefined)?.then === 'function';

/**
 * Calls `pollingAction` on every interval tick.
 *
 * When `pollingAction` returns a promise, a tick is skipped while that promise is pending, so a
 * slow poll is never overtaken by later polls. The skipped tick still counts towards `maxRetry`,
 * so polling ends in the same time as before even if a poll never answers. A poll held over from
 * before stopPolling or startPolling does not hold back the polls of the run that follows. The
 * action owns its error handling: a rejection only releases the next tick.
 * An action that returns nothing is called on every tick, as before.
 */
export default function usePolling({
  pollingAction,
  autoStart = true,
  pollingIntervalInMs = 1000,
  maxRetry = Number.MAX_VALUE,
  onMaxRetryReached,
}: {
  pollingAction: () => void | PromiseLike<unknown>;
  autoStart?: boolean;
  pollingIntervalInMs?: number;
  maxRetry?: number;
  onMaxRetryReached?: () => void;
}) {
  const [isKeepPolling, setIsKeepPolling] = useState(autoStart);
  const [retryCount, setRetryCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const [isInitialActionTriggered, setIsInitialActionTriggered] = useState(false);
  // Counts runs. startPolling and stopPolling each begin a new one, so a poll that is still in
  // flight from an earlier run neither holds back nor releases the polls of the current run.
  const runRef = useRef(0);
  const runOfPollInFlightRef = useRef<number | null>(null);

  const sendPoll = useCallback(() => {
    const run = runRef.current;
    if (runOfPollInFlightRef.current === run) {
      return;
    }
    const pending = pollingAction();
    if (!isPromiseLike(pending)) {
      return;
    }
    runOfPollInFlightRef.current = run;
    const release = () => {
      if (runOfPollInFlightRef.current === run) {
        runOfPollInFlightRef.current = null;
      }
    };
    pending.then(release, release);
  }, [pollingAction]);

  const startPolling = useCallback(() => {
    runRef.current += 1;
    setIsKeepPolling(true);
  }, []);

  const stopPolling = useCallback(() => {
    runRef.current += 1;
    setIsKeepPolling(false);
    setRetryCount(0);
  }, []);

  const isReachedRetryLimitation = useMemo(() => retryCount >= maxRetry, [retryCount, maxRetry]);

  useEffect(
    function checkRetryLimitation() {
      if (isReachedRetryLimitation) {
        setIsKeepPolling(false);
        onMaxRetryReached?.();
        // Reset retry count after reaching the limitation so that if user restarts polling, it can start fresh
        setRetryCount(0);
      }
    },
    [isReachedRetryLimitation, onMaxRetryReached],
  );

  useEffect(() => {
    if (isInitialActionTriggered || !autoStart) {
      return;
    }
    sendPoll();
    setIsInitialActionTriggered(true);
  }, [isInitialActionTriggered, sendPoll, autoStart]);

  useEffect(() => {
    if (!isKeepPolling) {
      clearInterval(timerRef.current);
      return;
    }

    if (isReachedRetryLimitation) {
      clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setRetryCount((retryCount) => retryCount + 1);
      sendPoll();
    }, pollingIntervalInMs);

    return () => clearInterval(timerRef.current);
  }, [isKeepPolling, sendPoll, pollingIntervalInMs, isReachedRetryLimitation]);

  return { startPolling, stopPolling };
}
